// Централизованный менеджер SignalR для оптимизации подключений
import * as signalR from '@microsoft/signalr';
import config from '../config/config';
import {
  getSignalRAccessToken,
  setupSignalRVisibilityReconnect,
  SIGNALR_KEEP_ALIVE_MS,
  SIGNALR_RECONNECT_DELAYS_MS,
  SIGNALR_SERVER_TIMEOUT_MS,
} from './signalRConnectionConfig';

// Функция для определения URL SignalR Hub
// ВАЖНО: SignalR использует https:// для negotiate запроса, затем автоматически переключается на wss:// для WebSocket
const getSignalRUrl = (): string => {
  // Если указана переменная окружения, используем её
  if (process.env.REACT_APP_SIGNALR_URL) {
    // Заменяем wss:// на https:// для negotiate запроса
    return process.env.REACT_APP_SIGNALR_URL.replace(/^wss:/, 'https:');
  }
  
  // Если указан API URL, используем его для SignalR (заменяем /api/v1 на /notificationHub)
  if (process.env.REACT_APP_API_URL) {
    const apiUrl = process.env.REACT_APP_API_URL;
    // Заменяем /api/v1 или /api/v2 на /notificationHub
    // Заменяем wss:// на https:// для negotiate запроса
    return apiUrl.replace(/\/api\/v[12]$/, '/notificationHub').replace(/^wss:/, 'https:');
  }
  
  // Используем базовый API URL из конфигурации и заменяем путь на /notificationHub
  const apiBaseUrl = config.api.baseURL;
  if (apiBaseUrl) {
    // Заменяем /api/v1 или /api/v2 на /notificationHub
    // Заменяем wss:// на https:// для negotiate запроса (SignalR сам переключится на wss:// для WebSocket)
    return apiBaseUrl.replace(/\/api\/v[12]$/, '/notificationHub').replace(/^wss:/, 'https:');
  }
  
  // Если мы в браузере и загружены не с localhost, используем текущий хост
  if (typeof window !== 'undefined') {
    const currentHost = window.location.hostname;
    const currentProtocol = window.location.protocol;
    
    // Если загружены через туннель, пытаемся использовать тот же домен для бэкенда
    const isTunnel = currentHost.includes('trycloudflare.com') || 
                     currentHost.includes('loca.lt') || 
                     currentHost.includes('ngrok.io') ||
                     currentHost.includes('ngrok-free.app');
    
    if (isTunnel) {
      // Пытаемся использовать тот же домен, но с путем /notificationHub
      // Это работает, если бэкенд настроен на проксирование через тот же туннель
      // Используем https:// для negotiate, SignalR переключится на wss:// для WebSocket
      const protocol = currentProtocol === 'https:' ? 'https:' : 'http:';
      const tunnelUrl = `${protocol}//${currentHost}/notificationHub`;
      console.warn('⚠️ Обнаружен туннель. Пытаемся подключиться к SignalR через туннель:', tunnelUrl);
      console.warn('💡 Если не работает, создайте отдельный туннель для бэкенда и установите REACT_APP_SIGNALR_URL');
      return tunnelUrl;
    }
    
    // Если загружены с домена ryaskov.pro, используем api.ryaskov.pro
    // ВАЖНО: Используем https:// для negotiate, SignalR переключится на wss:// для WebSocket
    if (currentHost === 'ryaskov.pro' || currentHost === 'www.ryaskov.pro') {
      return `https://api.ryaskov.pro/notificationHub`;
    }
    
    // Если загружены с IP адреса (не localhost и не 127.0.0.1), используем этот IP для SignalR
    // Проверяем, что это IP адрес (содержит точку, но не доменное имя)
    const isIPAddress = /^\d{1,3}(\.\d{1,3}){3}$/.test(currentHost);
    if (currentHost !== 'localhost' && currentHost !== '127.0.0.1' && isIPAddress) {
      // Если это IP сервера с nginx, используем тот же IP без порта (через nginx)
      const protocol = currentProtocol === 'https:' ? 'https:' : 'http:';
      return `${protocol}//${currentHost}/notificationHub`;
    }
  }
  
  // По умолчанию используем localhost
  return "http://localhost:5000/notificationHub";
};

// Типы для SignalR событий
export interface QuestionStatusChangedEvent {
  id: number;
  closed: boolean;
  siteId?: number;
  site?: {
    id: number;
    name: string;
    closed: boolean;
  };
  userId: number;
  text: string;
  visibilityType: number;
  createdAt?: string;
  user?: {
    id: number;
    name: string;
    closed: boolean;
  };
  typeOfWorks?: {
    id: number;
    name: string;
    section: string;
    closed: boolean;
  };
}

export interface QuestionMemberEvent {
  question: QuestionStatusChangedEvent;
  memberUserId: number;
}

export interface CommentEvent {
  id: number;
  questionId: number;
  userId: number;
  text: string;
  createdAt: string;
  isEdited: boolean;
  editedAt?: string;
}

export interface CommentDeletedEvent {
  commentId: number;
  questionId: number;
}

// Типы для событий смен
export interface SessionEvent {
  id: number;
  userId: number;
  siteId: number;
  typeOfWorks: number;
  timeStart: string;
  timeEnd?: string;
  hours?: number;
  rate?: number;
  contractId?: number;
  siteName?: string;
  siteClosed: boolean;
  userName?: string;
  userClosed: boolean;
  typeOfWorkName?: string;
  typeOfWorkClosed: boolean;
  typeOfWorkSection?: string;
  totalAmount?: number;
  paidAmount?: number;
  remainingAmount?: number;
  hasFinancialAccess: boolean;
}

// Типы обработчиков событий
export type QuestionStatusChangedHandler = (question: QuestionStatusChangedEvent) => void;
export type QuestionMemberHandler = (data: QuestionMemberEvent) => void;
export type QuestionCreatedHandler = (question: QuestionStatusChangedEvent) => void;
export type CommentCreatedHandler = (comment: CommentEvent) => void;
export type CommentUpdatedHandler = (comment: CommentEvent) => void;
export type CommentDeletedHandler = (data: CommentDeletedEvent) => void;
export type SessionCreatedHandler = (session: SessionEvent) => void;
export type SessionUpdatedHandler = (session: SessionEvent) => void;
export type SessionClosedHandler = (session: SessionEvent) => void;
export type SessionDeletedHandler = (session: SessionEvent) => void;

// Интерфейс для подписки на события
export interface SignalRSubscription {
  id: string;
  handlers: {
    onQuestionStatusChanged?: QuestionStatusChangedHandler;
    onQuestionMemberAdded?: QuestionMemberHandler;
    onQuestionMemberRemoved?: QuestionMemberHandler;
    onQuestionCreated?: QuestionCreatedHandler;
    onCommentCreated?: CommentCreatedHandler;
    onCommentUpdated?: CommentUpdatedHandler;
    onCommentDeleted?: CommentDeletedHandler;
    onSessionCreated?: SessionCreatedHandler;
    onSessionUpdated?: SessionUpdatedHandler;
    onSessionClosed?: SessionClosedHandler;
    onSessionDeleted?: SessionDeletedHandler;
  };
  siteId?: number;
  questionIds?: number[];
}

class SignalRManager {
  private connection: signalR.HubConnection | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  
  // Централизованные обработчики событий
  private eventHandlers = {
    QuestionStatusChanged: new Map<string, QuestionStatusChangedHandler>(),
    QuestionMemberAdded: new Map<string, QuestionMemberHandler>(),
    QuestionMemberRemoved: new Map<string, QuestionMemberHandler>(),
    QuestionCreated: new Map<string, QuestionCreatedHandler>(),
    CommentCreated: new Map<string, CommentCreatedHandler>(),
    CommentUpdated: new Map<string, CommentUpdatedHandler>(),
    CommentDeleted: new Map<string, CommentDeletedHandler>(),
    SessionCreated: new Map<string, SessionCreatedHandler>(),
    SessionUpdated: new Map<string, SessionUpdatedHandler>(),
    SessionClosed: new Map<string, SessionClosedHandler>(),
    SessionDeleted: new Map<string, SessionDeletedHandler>(),
  };

  // Отслеживание активных подписок
  private subscriptions = new Map<string, SignalRSubscription>();
  
  // Отслеживание подключенных групп
  private connectedGroups = new Set<string>();
  private connectedQuestionGroups = new Set<number>();
  
  // Дебаунсинг для disconnectFromUnusedGroups
  private disconnectTimeout: NodeJS.Timeout | null = null;
  private readonly DISCONNECT_DEBOUNCE_MS = 100; // 100ms задержка
  private visibilityCleanup: (() => void) | null = null;
  private starting = false;

  constructor() {
    this.visibilityCleanup = setupSignalRVisibilityReconnect(() => {
      void this.ensureConnected('visibility');
    });
    void this.init();
  }

  private async init() {
    if (this.starting) return;
    this.starting = true;

    try {
      if (this.connection) {
        try {
          await this.connection.stop();
        } catch {
          // ignore stop errors during re-init
        }
        this.connection = null;
      }

      const signalRUrl = getSignalRUrl();
      console.log('🔌 SignalR Manager URL:', signalRUrl);
      
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(signalRUrl, {
          accessTokenFactory: () => getSignalRAccessToken(),
        })
        .withAutomaticReconnect(SIGNALR_RECONNECT_DELAYS_MS)
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      this.connection.serverTimeoutInMilliseconds = SIGNALR_SERVER_TIMEOUT_MS;
      this.connection.keepAliveIntervalInMilliseconds = SIGNALR_KEEP_ALIVE_MS;

      // Настраиваем обработчики событий
      this.setupEventHandlers();

      // Подключаемся
      await this.connection.start();
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      console.log('🔌 SignalR Manager: подключение установлено');
      
      // Присоединяемся к базовой группе вопросов
      await this.joinGroup('questions');
      
    } catch (error) {
      console.error('❌ Ошибка подключения SignalR Manager:', error);
      this.isConnected = false;
      this.handleReconnect();
    } finally {
      this.starting = false;
    }
  }

  /** Переподключение при возврате вкладки или после onclose. */
  private async ensureConnected(reason: string) {
    if (this.starting) return;

    const state = this.connection?.state;
    if (state === signalR.HubConnectionState.Connected || state === signalR.HubConnectionState.Connecting) {
      return;
    }

    if (state === signalR.HubConnectionState.Reconnecting) {
      return;
    }

    console.log(`🔄 SignalR Manager: ensureConnected (${reason})`);
    await this.init();
  }

  private setupEventHandlers() {
    if (!this.connection) return;

    // Централизованные обработчики событий
    this.connection.on("QuestionStatusChanged", (question: QuestionStatusChangedEvent) => {
      console.log('🔔 SignalR Manager: QuestionStatusChanged', question);
      this.notifyHandlers('QuestionStatusChanged', question);
    });

    this.connection.on("QuestionMemberAdded", (data: QuestionMemberEvent) => {
      console.log('🔔 SignalR Manager: QuestionMemberAdded', data);
      this.notifyHandlers('QuestionMemberAdded', data);
    });

    this.connection.on("QuestionMemberRemoved", (data: QuestionMemberEvent) => {
      console.log('🔔 SignalR Manager: QuestionMemberRemoved', data);
      this.notifyHandlers('QuestionMemberRemoved', data);
    });

    this.connection.on("QuestionCreated", (question: QuestionStatusChangedEvent) => {
      console.log('🔔 SignalR Manager: QuestionCreated', question);
      this.notifyHandlers('QuestionCreated', question);
    });

    this.connection.on("CommentCreated", (comment: CommentEvent) => {
      console.log('🔔 SignalR Manager: CommentCreated', comment);
      this.notifyHandlers('CommentCreated', comment);
    });

    this.connection.on("CommentUpdated", (comment: CommentEvent) => {
      console.log('🔔 SignalR Manager: CommentUpdated', comment);
      this.notifyHandlers('CommentUpdated', comment);
    });

    this.connection.on("CommentDeleted", (commentId: number, questionId: number) => {
      console.log('🔔 SignalR Manager: CommentDeleted', { commentId, questionId });
      this.notifyHandlers('CommentDeleted', { commentId, questionId });
    });

    // Обработчики для смен
    this.connection.on("SessionCreated", (session: SessionEvent) => {
      console.log('🔔 SignalR Manager: SessionCreated', session);
      if (session) {
        this.notifyHandlers('SessionCreated', session);
      } else {
        console.warn('⚠️ SessionCreated: получены пустые данные');
      }
    });

    this.connection.on("SessionUpdated", (session: SessionEvent) => {
      console.log('🔔 SignalR Manager: SessionUpdated', session);
      if (session) {
        this.notifyHandlers('SessionUpdated', session);
      } else {
        console.warn('⚠️ SessionUpdated: получены пустые данные');
      }
    });

    this.connection.on("SessionClosed", (session: SessionEvent) => {
      console.log('🔔 SignalR Manager: SessionClosed', session);
      if (session) {
        this.notifyHandlers('SessionClosed', session);
      } else {
        console.warn('⚠️ SessionClosed: получены пустые данные');
      }
    });

    this.connection.on("SessionDeleted", (session: SessionEvent) => {
      console.log('🔔 SignalR Manager: SessionDeleted', session);
      if (session) {
        this.notifyHandlers('SessionDeleted', session);
      } else {
        console.warn('⚠️ SessionDeleted: получены пустые данные');
      }
    });

    // Обработчики подключения
    this.connection.onreconnecting((error) => {
      console.log('🔄 SignalR Manager: переподключение...', error);
      this.isConnected = false;
    });

    this.connection.onreconnected((connectionId) => {
      console.log('✅ SignalR Manager: переподключен:', connectionId);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      // Восстанавливаем подключения к группам
      this.restoreGroupConnections();
    });

    this.connection.onclose((error) => {
      console.log('❌ SignalR Manager: соединение закрыто:', error);
      this.isConnected = false;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        // В фоне браузер часто режет WebSocket — переподключимся при возврате на вкладку.
        return;
      }
      this.handleReconnect();
    });
  }

  private async restoreGroupConnections() {
    // Восстанавливаем подключения к группам сайтов последовательно
    const groupsArray = Array.from(this.connectedGroups);
    for (const groupName of groupsArray) {
      await this.joinGroup(groupName);
    }
    
    // Восстанавливаем подключения к группам вопросов последовательно
    const questionGroupsArray = Array.from(this.connectedQuestionGroups);
    for (const questionId of questionGroupsArray) {
      await this.joinQuestionGroup(questionId);
    }
  }

  private handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      
      console.log(`🔄 SignalR Manager: попытка переподключения ${this.reconnectAttempts}/${this.maxReconnectAttempts} через ${delay}ms`);
      
      setTimeout(() => {
        this.init();
      }, delay);
    } else {
      console.error('❌ SignalR Manager: максимальное количество попыток переподключения достигнуто');
    }
  }

  // Уведомление всех обработчиков события
  private notifyHandlers(eventName: keyof typeof this.eventHandlers, data: any) {
    const handlers = this.eventHandlers[eventName];
    handlers.forEach((handler, subscriptionId) => {
      try {
        const subscription = this.subscriptions.get(subscriptionId);
        if (subscription) {
          // Фильтруем события по siteId и questionIds если нужно
          if (this.shouldNotifySubscription(subscription, data)) {
            handler(data);
          }
        }
      } catch (error) {
        console.error(`❌ Ошибка в обработчике события ${eventName} для подписки ${subscriptionId}:`, error);
      }
    });
  }

  // Проверяем, нужно ли уведомлять конкретную подписку
  // После оптимизации с группами эта проверка в основном нужна для совместимости
  // и для случаев, когда событие не попадает в нужную группу
  private shouldNotifySubscription(subscription: SignalRSubscription, data: any): boolean {
    // Защита от null/undefined данных
    if (!data) {
      console.warn('⚠️ shouldNotifySubscription: получены пустые данные');
      return false;
    }

    // Если указан siteId, проверяем соответствие (дополнительная проверка безопасности)
    if (subscription.siteId) {
      // Для вопросов - проверяем siteId из разных полей
      const questionSiteId = data.question?.site?.id || data.site?.id || data.siteId;
      if (questionSiteId !== undefined && questionSiteId !== subscription.siteId) {
        return false;
      }
      
      // Для смен - проверяем, что это смена для нужного объекта
      if (data.siteId !== undefined && data.siteId !== subscription.siteId) {
        return false;
      }
    }

    // Если указаны questionIds, проверяем соответствие
    if (subscription.questionIds && subscription.questionIds.length > 0) {
      const questionId = data.questionId || data.question?.id || data.id;
      if (questionId && !subscription.questionIds.includes(questionId)) {
        return false;
      }
    }

    return true;
  }

  // Регистрация подписки
  subscribe(subscription: SignalRSubscription): string {
    const subscriptionId = subscription.id;
    
    // Отладочная информация
    console.log('📡 SignalR Manager: попытка регистрации подписки', {
      id: subscriptionId,
      siteId: subscription.siteId,
      questionIds: subscription.questionIds,
      existingSubscriptions: this.subscriptions.size,
      stackTrace: new Error().stack?.split('\n').slice(1, 4).join('\n')
    });
    
    // Проверяем, есть ли уже такая подписка
    if (this.subscriptions.has(subscriptionId)) {
      console.log('⚠️ SignalR Manager: подписка уже существует, обновляем', subscriptionId);
    } else {
      console.log('✅ SignalR Manager: новая подписка', subscriptionId);
    }
    
    // Сохраняем подписку
    this.subscriptions.set(subscriptionId, subscription);
    
    // Регистрируем обработчики
    if (subscription.handlers.onQuestionStatusChanged) {
      this.eventHandlers.QuestionStatusChanged.set(subscriptionId, subscription.handlers.onQuestionStatusChanged);
    }
    if (subscription.handlers.onQuestionMemberAdded) {
      this.eventHandlers.QuestionMemberAdded.set(subscriptionId, subscription.handlers.onQuestionMemberAdded);
    }
    if (subscription.handlers.onQuestionMemberRemoved) {
      this.eventHandlers.QuestionMemberRemoved.set(subscriptionId, subscription.handlers.onQuestionMemberRemoved);
    }
    if (subscription.handlers.onQuestionCreated) {
      this.eventHandlers.QuestionCreated.set(subscriptionId, subscription.handlers.onQuestionCreated);
    }
    if (subscription.handlers.onCommentCreated) {
      this.eventHandlers.CommentCreated.set(subscriptionId, subscription.handlers.onCommentCreated);
    }
    if (subscription.handlers.onCommentUpdated) {
      this.eventHandlers.CommentUpdated.set(subscriptionId, subscription.handlers.onCommentUpdated);
    }
    if (subscription.handlers.onCommentDeleted) {
      this.eventHandlers.CommentDeleted.set(subscriptionId, subscription.handlers.onCommentDeleted);
    }
    if (subscription.handlers.onSessionCreated) {
      this.eventHandlers.SessionCreated.set(subscriptionId, subscription.handlers.onSessionCreated);
    }
    if (subscription.handlers.onSessionUpdated) {
      this.eventHandlers.SessionUpdated.set(subscriptionId, subscription.handlers.onSessionUpdated);
    }
    if (subscription.handlers.onSessionClosed) {
      this.eventHandlers.SessionClosed.set(subscriptionId, subscription.handlers.onSessionClosed);
    }
    if (subscription.handlers.onSessionDeleted) {
      this.eventHandlers.SessionDeleted.set(subscriptionId, subscription.handlers.onSessionDeleted);
    }

    // Подключаемся к необходимым группам
    this.connectToRequiredGroups(subscription);

    console.log(`📡 SignalR Manager: зарегистрирована подписка ${subscriptionId}`);
    return subscriptionId;
  }

  // Отмена подписки
  unsubscribe(subscriptionId: string) {
    const subscription = this.subscriptions.get(subscriptionId);
    if (!subscription) {
      console.log('⚠️ SignalR Manager: попытка отмены несуществующей подписки', subscriptionId);
      return;
    }

    // Отладочная информация
    console.log('📡 SignalR Manager: отмена подписки', {
      id: subscriptionId,
      siteId: subscription.siteId,
      questionIds: subscription.questionIds,
      remainingSubscriptions: this.subscriptions.size - 1,
      stackTrace: new Error().stack?.split('\n').slice(1, 4).join('\n')
    });

    // Удаляем обработчики
    this.eventHandlers.QuestionStatusChanged.delete(subscriptionId);
    this.eventHandlers.QuestionMemberAdded.delete(subscriptionId);
    this.eventHandlers.QuestionMemberRemoved.delete(subscriptionId);
    this.eventHandlers.QuestionCreated.delete(subscriptionId);
    this.eventHandlers.CommentCreated.delete(subscriptionId);
    this.eventHandlers.CommentUpdated.delete(subscriptionId);
    this.eventHandlers.CommentDeleted.delete(subscriptionId);
    this.eventHandlers.SessionCreated.delete(subscriptionId);
    this.eventHandlers.SessionUpdated.delete(subscriptionId);
    this.eventHandlers.SessionClosed.delete(subscriptionId);
    this.eventHandlers.SessionDeleted.delete(subscriptionId);

    // Удаляем подписку
    this.subscriptions.delete(subscriptionId);

    // Отключаемся от групп если они больше не нужны (с дебаунсингом)
    this.scheduleDisconnectFromUnusedGroups();

    console.log(`📡 SignalR Manager: отменена подписка ${subscriptionId}`);
  }
  
  // Планирование отключения от неиспользуемых групп с дебаунсингом
  private scheduleDisconnectFromUnusedGroups() {
    // Отменяем предыдущий таймер, если он есть
    if (this.disconnectTimeout) {
      clearTimeout(this.disconnectTimeout);
    }
    
    // Планируем отключение через небольшую задержку
    // Это позволяет собрать все изменения подписок вместе
    this.disconnectTimeout = setTimeout(() => {
      this.disconnectFromUnusedGroups();
      this.disconnectTimeout = null;
    }, this.DISCONNECT_DEBOUNCE_MS);
  }

  // Подключение к необходимым группам
  private async connectToRequiredGroups(subscription: SignalRSubscription) {
    // Подключаемся к группе сайта для вопросов
    if (subscription.siteId) {
      const siteQuestionsGroupName = `site_${subscription.siteId}_questions`;
      if (!this.connectedGroups.has(siteQuestionsGroupName)) {
        this.connectedGroups.add(siteQuestionsGroupName); // Добавляем ПЕРЕД вызовом joinGroup
        await this.joinGroup(siteQuestionsGroupName);
      }
      
      // Подключаемся к группе сайта для смен (если есть обработчики смен)
      const hasSessionHandlers = subscription.handlers.onSessionCreated || 
                                  subscription.handlers.onSessionUpdated || 
                                  subscription.handlers.onSessionClosed || 
                                  subscription.handlers.onSessionDeleted;
      
      if (hasSessionHandlers) {
        const siteSessionsGroupName = `site_${subscription.siteId}`;
        if (!this.connectedGroups.has(siteSessionsGroupName)) {
          this.connectedGroups.add(siteSessionsGroupName); // Добавляем ПЕРЕД вызовом joinGroup
          await this.joinGroup(siteSessionsGroupName);
        }
      }
    }

    // Подключаемся к группам вопросов
    if (subscription.questionIds) {
      for (const questionId of subscription.questionIds) {
        if (!this.connectedQuestionGroups.has(questionId)) {
          this.connectedQuestionGroups.add(questionId); // Добавляем ПЕРЕД вызовом joinQuestionGroup
          await this.joinQuestionGroup(questionId);
        }
      }
    }
  }

  // Отключение от неиспользуемых групп
  private async disconnectFromUnusedGroups() {
    // Проверяем группы сайтов (и для вопросов, и для смен)
    const usedSiteGroups = new Set<string>();
    this.subscriptions.forEach((subscription) => {
      if (subscription.siteId) {
        // Группа для вопросов
        usedSiteGroups.add(`site_${subscription.siteId}_questions`);
        
        // Группа для смен (если есть обработчики смен)
        const hasSessionHandlers = subscription.handlers.onSessionCreated || 
                                    subscription.handlers.onSessionUpdated || 
                                    subscription.handlers.onSessionClosed || 
                                    subscription.handlers.onSessionDeleted;
        
        if (hasSessionHandlers) {
          usedSiteGroups.add(`site_${subscription.siteId}`);
        }
      }
    });

    // Находим группы для отключения
    const groupsToLeave: string[] = [];
    this.connectedGroups.forEach((groupName) => {
      if (!usedSiteGroups.has(groupName)) {
        groupsToLeave.push(groupName);
      }
    });
    
    // Удаляем группы из Set ПЕРЕД отключением, чтобы избежать дублирования
    groupsToLeave.forEach((groupName) => {
      this.connectedGroups.delete(groupName);
    });
    
    // Отключаемся от групп последовательно
    for (const groupName of groupsToLeave) {
      await this.leaveGroup(groupName);
    }

    // Проверяем группы вопросов
    const usedQuestionGroups = new Set<number>();
    this.subscriptions.forEach((subscription) => {
      if (subscription.questionIds) {
        subscription.questionIds.forEach((id: number) => usedQuestionGroups.add(id));
      }
    });

    // Находим группы вопросов для отключения
    const questionGroupsToLeave: number[] = [];
    this.connectedQuestionGroups.forEach((questionId) => {
      if (!usedQuestionGroups.has(questionId)) {
        questionGroupsToLeave.push(questionId);
      }
    });
    
    // Удаляем группы из Set ПЕРЕД отключением, чтобы избежать дублирования
    questionGroupsToLeave.forEach((questionId) => {
      this.connectedQuestionGroups.delete(questionId);
    });
    
    // Отключаемся от групп вопросов последовательно
    for (const questionId of questionGroupsToLeave) {
      await this.leaveQuestionGroup(questionId);
    }
  }

  // Присоединение к группе
  private async joinGroup(groupName: string) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("JoinGroup", groupName);
        console.log(`📡 SignalR Manager: присоединились к группе: ${groupName}`);
      } catch (error) {
        console.error(`❌ Ошибка присоединения к группе ${groupName}:`, error);
      }
    }
  }

  // Присоединение к группе вопроса
  private async joinQuestionGroup(questionId: number) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("JoinQuestionGroup", questionId);
        console.log(`❓ SignalR Manager: присоединились к группе вопроса: ${questionId}`);
      } catch (error) {
        console.error(`❌ Ошибка присоединения к группе вопроса ${questionId}:`, error);
      }
    }
  }

  // Отключение от группы
  private async leaveGroup(groupName: string) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("LeaveGroup", groupName);
        console.log(`📡 SignalR Manager: отключились от группы: ${groupName}`);
      } catch (error) {
        console.error(`❌ Ошибка отключения от группы ${groupName}:`, error);
      }
    }
  }

  // Отключение от группы вопроса
  private async leaveQuestionGroup(questionId: number) {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke("LeaveQuestionGroup", questionId);
        console.log(`❓ SignalR Manager: отключились от группы вопроса: ${questionId}`);
      } catch (error) {
        console.error(`❌ Ошибка отключения от группы вопроса ${questionId}:`, error);
      }
    }
  }

  // Отключение
  async disconnect() {
    this.visibilityCleanup?.();
    this.visibilityCleanup = null;

    if (this.connection) {
      try {
        await this.connection.stop();
        this.isConnected = false;
        this.connectedGroups.clear();
        this.connectedQuestionGroups.clear();
        console.log('🔌 SignalR Manager: отключен');
      } catch (error) {
        console.error('❌ Ошибка отключения SignalR Manager:', error);
      }
    }
  }

  // Геттеры
  get connectionState() {
    return this.connection?.state || signalR.HubConnectionState.Disconnected;
  }

  get connected() {
    return this.isConnected;
  }

  get activeSubscriptions() {
    return this.subscriptions.size;
  }

  get connectedGroupsCount() {
    return this.connectedGroups.size + this.connectedQuestionGroups.size;
  }
}

// Создаем глобальный экземпляр SignalR менеджера
const signalRManager = new SignalRManager();

// Делаем его доступным глобально
(window as any).signalRManager = signalRManager;

export default signalRManager;
